<template>
  <div class="payment-button-wrapper">
    <!-- reCAPTCHA v2 visible checkbox (if enabled) -->
    <div
      v-if="enableRecaptcha && recaptchaVersion === 'v2'"
      class="recaptcha-container"
    >
      <div
        :id="recaptchaElementId"
        class="recaptcha-checkbox"
      />
    </div>

    <div class="payment-button">
      <b-btn
        :class="{ 'payment-disabled' : disableButton }"
        :disabled="disableButton"
        variant="primary"
        data-cy="payment-button"
        class="payment-button"
        @click="buttonClick"
      >
        {{ currentButtonLabel }}
      </b-btn>
      <b-tooltip
        v-if="currentTooltipMessage"
        target="recaptcha-button"
        placement="bottom"
        triggers="hover"
      >
        {{ currentTooltipMessage }}
      </b-tooltip>
    </div>
  </div>
</template>

<script>
export default {
  name: 'payment-button',
  props: {
    label: {
      type: String,
      required: false,
      default: 'Process Payment'
    },
    recaptcha: {
      type: [Boolean, Object],
      required: false,
      default: () => false
      // Expected format:
      // { key: 'your-site-key', version: 'v2' | 'v3' }
      // Legacy format still supported:
      // { key: 'your-site-key', showRecaptchaButton: true } (implies v2)
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    tooltipMessage: {
      type: String,
      required: false,
      default: 'complete the form details'
    },
    paymentTesting: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function() {
    return {
      recaptchaResponse: null,
      // recaptchaInstance: null,
      recaptchaLoaded: false,
      recaptchaLoadError: null,
      recaptchaV2WidgetId: null,
      recaptchaV2Response: null
    }
  },
  computed: {
    testingEnabled: function() {
      return this.paymentTesting
    },
    disableButton: function() {
      // Disable if explicitly disabled via prop
      if (this.disabled) {
        return true
      }

      // Disable if reCAPTCHA is enabled but not yet loaded (and not in error state)
      if (this.enableRecaptcha && !this.recaptchaLoaded && !this.recaptchaLoadError) {
        return true
      }

      return false
    },
    currentTooltipMessage: function() {
      // Show reCAPTCHA error message if loading failed
      if (this.enableRecaptcha && this.recaptchaLoadError) {
        return `reCAPTCHA failed to load: ${this.recaptchaLoadError}`
      }

      // Show reCAPTCHA loading message if it's enabled but not loaded
      if (this.enableRecaptcha && !this.recaptchaLoaded && !this.recaptchaLoadError) {
        return `Loading reCAPTCHA ${this.recaptchaVersion}...`
      }

      // Show custom tooltip message if provided and button is disabled for other reasons
      if (this.disabled && this.tooltipMessage) {
        return this.tooltipMessage
      }

      // No tooltip if button is enabled and reCAPTCHA is ready
      return null
    },
    currentButtonLabel: function() {
      // Show error status if reCAPTCHA failed to load
      if (this.enableRecaptcha && this.recaptchaLoadError) {
        return 'reCAPTCHA Load Failed'
      }

      // Show loading status if reCAPTCHA is enabled but not loaded
      if (this.enableRecaptcha && !this.recaptchaLoaded && !this.recaptchaLoadError) {
        return `Loading reCAPTCHA ${this.recaptchaVersion}...`
      }

      // Default label
      return this.label
    },
    recaptchaKey: function() {
      return (this.recaptcha && Object.prototype.hasOwnProperty.call(this.recaptcha, 'key')) ? this.recaptcha.key : null
    },
    enableRecaptcha: function() {
      return (typeof this.recaptchaKey === 'string' && this.recaptchaKey.length > 0)
    },
    recaptchaElementId: function() {
      return `recaptcha-${this._uid}`
    },
    recaptchaVersion: function() {
      // Use version from recaptcha prop, fallback to 'v3' as default
      if (this.recaptcha && this.recaptcha.version) {
        return this.recaptcha.version
      }
      // Legacy fallback: if showRecaptchaButton is true, assume v2
      if (this.showRecaptchaButton) {
        return 'v2'
      }
      return 'v3'
    }
  },
  async mounted() {
    // Initialize reCAPTCHA in this component
    if (this.enableRecaptcha) {
      try {
        console.debug(`Initializing reCAPTCHA ${this.recaptchaVersion} with key: ${this.recaptchaKey.substring(0, 8)}...`)

        if (this.recaptchaVersion === 'v2') {
          console.debug('Loading reCAPTCHA v2 for checkbox display')
          // Load reCAPTCHA v2 for visible checkbox
          await this.loadReCaptchaV2()
          this.renderRecaptchaV2()
        }
        else {
          console.debug('Loading reCAPTCHA v3 for invisible verification')
          // Load reCAPTCHA v3 dynamically (invisible)
          await this.loadReCaptcha()

          // Test if reCAPTCHA v3 is actually ready after loading
          if (window.grecaptcha && window.grecaptcha.execute) {
            console.debug('reCAPTCHA v3 execute method is available')
          }
          else {
            console.warn('reCAPTCHA v3 loaded but execute method is not available')
          }
        }
        this.recaptchaLoaded = true
        console.debug(`reCAPTCHA ${this.recaptchaVersion} Support Added to the form`)
      }
      catch (error) {
        console.error(`Failed to load reCAPTCHA ${this.recaptchaVersion}:`, error)

        // Set a flag to indicate reCAPTCHA failed to load
        this.recaptchaLoadError = error.message

        if (this.recaptchaVersion === 'v2') {
          console.error('Make sure you are using a reCAPTCHA v2 site key, not a v3 key!')
          console.info('Get a v2 key from: https://www.google.com/recaptcha/admin/create')
          console.info('For testing, you can use: 6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI')
        }
        else {
          console.error('reCAPTCHA v3 failed to load. Possible causes:')
          console.info('1. Network connectivity issues')
          console.info('2. Ad blocker or firewall blocking Google reCAPTCHA')
          console.info('3. Invalid site key or domain not authorized')
          console.info('4. Content Security Policy restrictions')
          console.info('Try refreshing the page or disabling ad blockers.')
        }

        // Emit error event to parent component
        this.$emit('recaptchaLoadError', error.message)
      }
    }
    else {
      console.debug('reCAPTCHA not enabled, skipping initialization')
    }
  },
  beforeDestroy() {
    if (this.recaptchaV2WidgetId !== null && window.grecaptcha && window.grecaptcha.reset) {
      window.grecaptcha.reset(this.recaptchaV2WidgetId)
    }
  },
  methods: {
    loadReCaptchaV2() {
      return new Promise((resolve, reject) => {
        // Check if reCAPTCHA is already loaded
        if (window.grecaptcha && window.grecaptcha.render) {
          resolve(window.grecaptcha)
          return
        }

        // Check if script is already being loaded
        const existingScript = document.querySelector('script[src*="recaptcha/api.js"]')
        if (existingScript) {
          // Wait for existing script to load
          const checkLoaded = () => {
            if (window.grecaptcha && window.grecaptcha.render) {
              resolve(window.grecaptcha)
            }
            else {
              setTimeout(checkLoaded, 100)
            }
          }
          checkLoaded()
          return
        }

        // Create a unique callback name to avoid conflicts
        const callbackName = `onRecaptchaLoad_${this._uid}`

        // Create a global callback for when reCAPTCHA is ready
        window[callbackName] = () => {
          if (window.grecaptcha && window.grecaptcha.render) {
            // Clean up the callback
            delete window[callbackName]
            resolve(window.grecaptcha)
          }
          else {
            delete window[callbackName]
            reject(new Error('reCAPTCHA v2 failed to load'))
          }
        }

        // Load the reCAPTCHA v2 script with callback
        const script = document.createElement('script')
        script.src = `https://www.google.com/recaptcha/api.js?onload=${callbackName}&render=explicit`
        script.onerror = () => {
          delete window[callbackName]
          reject(new Error('Failed to load reCAPTCHA v2 script'))
        }
        document.head.appendChild(script)
      })
    },
    renderRecaptchaV2() {
      this.$nextTick(() => {
        const element = document.getElementById(this.recaptchaElementId)
        if (element && window.grecaptcha && window.grecaptcha.render) {
          try {
            this.recaptchaV2WidgetId = window.grecaptcha.render(this.recaptchaElementId, {
              sitekey: this.recaptchaKey,
              callback: this.onRecaptchaV2Verify,
              'expired-callback': this.onRecaptchaV2Expired,
              'error-callback': this.onRecaptchaV2Error
            })
            console.debug('reCAPTCHA v2 widget rendered successfully')
          }
          catch (error) {
            console.error('Failed to render reCAPTCHA v2 widget:', error)
          }
        }
        else {
          console.error('Cannot render reCAPTCHA v2: element or grecaptcha not available')
        }
      })
    },
    onRecaptchaV2Verify(response) {
      this.recaptchaV2Response = response
      console.debug('reCAPTCHA v2 verified:', response)
      // Emit verification event to parent component
      this.$emit('recaptchaVerified', response)
    },
    onRecaptchaV2Expired() {
      this.recaptchaV2Response = null
      console.debug('reCAPTCHA v2 expired')
      // Emit expired event to parent component
      this.$emit('recaptchaExpired')
      this.$emit('recaptchaVerified', null)
    },
    onRecaptchaV2Error(error) {
      this.recaptchaV2Response = null
      console.error('reCAPTCHA v2 error:', error)
      // Emit error event to parent component
      this.$emit('recaptchaError', error)
      this.$emit('recaptchaVerified', null)
    },
    loadReCaptcha() {
      return new Promise((resolve, reject) => {
        // Check if reCAPTCHA is already loaded and ready
        if (window.grecaptcha && window.grecaptcha.ready && window.grecaptcha.execute) {
          window.grecaptcha.ready(() => {
            resolve(window.grecaptcha)
          })
          return
        }

        // Check if script is already being loaded
        const existingScript = document.querySelector(`script[src*="render=${this.recaptchaKey}"]`)
        if (existingScript) {
          // Wait for existing script to load with timeout
          let attempts = 0
          const maxAttempts = 100 // 10 seconds max wait time
          const checkLoaded = () => {
            if (window.grecaptcha && window.grecaptcha.ready && window.grecaptcha.execute) {
              window.grecaptcha.ready(() => {
                resolve(window.grecaptcha)
              })
            }
            else {
              attempts++
              if (attempts >= maxAttempts) {
                reject(new Error('Existing reCAPTCHA script failed to load within timeout period'))
                return
              }
              setTimeout(checkLoaded, 100)
            }
          }
          checkLoaded()
          return
        }

        let loadAttempts = 0
        const maxLoadAttempts = 3

        const attemptLoad = () => {
          loadAttempts++
          console.debug(`Loading reCAPTCHA v3 script (attempt ${loadAttempts}/${maxLoadAttempts})`)

          // Load the reCAPTCHA v3 script
          const script = document.createElement('script')
          script.src = `https://www.google.com/recaptcha/api.js?render=${this.recaptchaKey}`
          script.async = true
          script.defer = true

          script.onload = () => {
            console.debug('reCAPTCHA v3 script loaded, waiting for initialization...')
            // Wait for grecaptcha to be fully ready with timeout
            let attempts = 0
            const maxAttempts = 50 // 5 seconds max wait time
            const checkReady = () => {
              if (window.grecaptcha && window.grecaptcha.ready && window.grecaptcha.execute) {
                window.grecaptcha.ready(() => {
                  console.debug('reCAPTCHA v3 loaded and ready')
                  resolve(window.grecaptcha)
                })
              }
              else {
                attempts++
                if (attempts >= maxAttempts) {
                  console.error('reCAPTCHA v3 script loaded but failed to initialize')
                  if (loadAttempts < maxLoadAttempts) {
                    console.debug(`Retrying reCAPTCHA load in 2 seconds...`)
                    // Remove failed script
                    script.remove()
                    setTimeout(attemptLoad, 2000)
                  }
                  else {
                    reject(new Error('reCAPTCHA v3 failed to initialize after multiple attempts'))
                  }
                  return
                }
                setTimeout(checkReady, 100)
              }
            }
            checkReady()
          }

          script.onerror = (error) => {
            console.error(`Failed to load reCAPTCHA v3 script (attempt ${loadAttempts}):`, error)
            clearTimeout(scriptTimeout)
            script.remove()

            if (loadAttempts < maxLoadAttempts) {
              console.debug(`Retrying reCAPTCHA load in 2 seconds...`)
              setTimeout(attemptLoad, 2000)
            }
            else {
              const errorMessage = 'Failed to load reCAPTCHA v3 script after multiple attempts. ' +
                'This could be due to network issues, ad blockers, or firewall restrictions. ' +
                'Please check your internet connection and try refreshing the page.'
              reject(new Error(errorMessage))
            }
          }

          // Set a timeout for the script load
          const scriptTimeout = setTimeout(() => {
            console.error(`reCAPTCHA script load timeout (attempt ${loadAttempts})`)
            script.remove()

            if (loadAttempts < maxLoadAttempts) {
              console.debug(`Retrying reCAPTCHA load in 2 seconds...`)
              setTimeout(attemptLoad, 2000)
            }
            else {
              reject(new Error('reCAPTCHA v3 script load timeout after multiple attempts'))
            }
          }, 10000) // 10 second timeout per attempt

          // Override the onload to clear timeout
          const originalOnload = script.onload
          script.onload = () => {
            clearTimeout(scriptTimeout)
            originalOnload()
          }

          document.head.appendChild(script)
        }

        attemptLoad()
      })
    },
    processPayment: async function() {
      try {
        let recaptchaToken = ''

        if (this.enableRecaptcha && this.recaptchaLoaded) {
          if (this.recaptchaVersion === 'v2') {
            // Use reCAPTCHA v2 response
            if (!this.recaptchaV2Response) {
              throw new Error('Please complete the reCAPTCHA verification')
            }
            recaptchaToken = this.recaptchaV2Response
            // Note: recaptchaVerified event is already emitted in onRecaptchaV2Verify
          }
          else {
            // Execute reCAPTCHA v3 (invisible)
            if (!window.grecaptcha || !window.grecaptcha.execute) {
              throw new Error('reCAPTCHA v3 is not ready')
            }

            console.debug('Executing reCAPTCHA v3 verification...')
            console.debug('Site key:', this.recaptchaKey.substring(0, 8) + '...')
            console.debug('Current domain:', window.location.hostname)

            // Check if using test key on non-localhost domain
            const isTestKey = this.recaptchaKey === '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

            if (isTestKey && !isLocalhost) {
              console.warn('Using Google test reCAPTCHA key on non-localhost domain. This will fail.')
              console.info('Options:')
              console.info('1. Get a proper reCAPTCHA key for your domain at: https://www.google.com/recaptcha/admin/create')
              console.info('2. Add your domain to the reCAPTCHA key configuration')
              console.info('3. Test on localhost instead')
            }

            try {
              const token = await window.grecaptcha.execute(this.recaptchaKey, { action: 'checkout' })
              console.debug('reCAPTCHA execute returned:', token ? 'valid token' : 'null/undefined')

              if (!token || typeof token !== 'string' || token.length === 0) {
                let errorMessage = 'reCAPTCHA v3 execute returned an invalid token.'

                if (isTestKey && !isLocalhost) {
                  errorMessage += ' The Google test key only works on localhost. Please use a proper site key for this domain or test on localhost.'
                }
                else {
                  errorMessage += ' Check if the site key is correct and the domain is authorized.'
                }

                throw new Error(errorMessage)
              }

              recaptchaToken = token
              console.debug('reCAPTCHA v3 token obtained successfully')
              // Emit verification event for v3
              this.$emit('recaptchaVerified', token)
            }
            catch (executeError) {
              console.error('reCAPTCHA execute error:', executeError)
              throw new Error(`reCAPTCHA v3 execution failed: ${executeError.message}`)
            }
          }
        }
        else if (this.enableRecaptcha && !this.recaptchaLoaded) {
          // Try to wait a bit longer for reCAPTCHA to load
          console.debug('reCAPTCHA not loaded yet, waiting 1 second and retrying...')
          await new Promise(resolve => setTimeout(resolve, 1000))

          if (!this.recaptchaLoaded) {
            throw new Error('reCAPTCHA is taking too long to load. Please refresh the page and try again.')
          }

          // Retry the reCAPTCHA execution after waiting
          return this.processPayment()
        }
        else if (!this.enableRecaptcha) {
          // Emit null when reCAPTCHA is not enabled
          this.$emit('recaptchaVerified', null)
        }

        console.debug(`reCAPTCHA token: ${recaptchaToken.substring(0, 20)}...`)
        // Send form data with token to your backend
        this.$emit('click', recaptchaToken)
      }
      catch (error) {
        console.error('reCAPTCHA error:', error)
        this.$emit('error', `reCAPTCHA error: ${error.message}`)
        // Emit null on error
        this.$emit('recaptchaVerified', null)
      }
    },
    buttonClick: function() {
      this.processPayment()
    }
  }

}
</script>

<style scoped lang="scss">
  .payment-button-wrapper, .payment-button {
    width:100%;
    margin:1rem auto autow auto;
    padding:1em;
  }

  .recaptcha-container {
    margin: 1rem 0;
    display: flex;
    justify-content: center;

    .recaptcha-checkbox {
      margin: 0 auto;
    }
  }

  button.payment-button {
    margin: 3rem 0 0 0;
  }
</style>
